'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/app-badge';
import { cn } from '@/lib/ui/utils';

/**
 * The per-entity tag pill shown at the right edge of a search result row.
 *
 * Colour mapping (search-overhaul mockup states 02–07):
 *   - market    → blue   (`outcome-blue` role — theme-aware, readable on dark)
 *   - outcome   → amber  (`outcome-amber` role — theme-aware, readable on dark)
 *   - winner    → green  (`--success` semantic)
 *   - launchpad → purple (`outcome-purple` role — theme-aware, readable on dark)
 *   - trader    → neutral outline
 *   - creator   → neutral outline
 *   - partner   → partner product colours (admin-managed creator)
 *
 * The market / outcome / launchpad tags use the theme-aware `outcome-*` role
 * pairs rather than the flat `--info` / `--warning` / `--launchpad` semantics: in
 * dark mode those resolve to step-700 inks (`blue-700` / `yellow-700` /
 * `purple-700`) that are too dark to read as tag text, whereas the `-text` role
 * brightens to step-400.
 */
export type EntityTagKind = 'market' | 'outcome' | 'winner' | 'launchpad' | 'trader' | 'creator' | 'partner';
const TINT_CLASS: Record<EntityTagKind, string> = {
  market: 'border-0 bg-outcome-blue-outline text-outcome-blue-text',
  outcome: 'border-0 bg-outcome-amber-outline text-outcome-amber-text',
  winner: 'border-0 bg-feedback-success-accent/10 text-feedback-success-content',
  launchpad: 'border-0 bg-outcome-purple-outline text-outcome-purple-text',
  trader: '',
  creator: '',
  partner: 'border-product-partner-border bg-product-partner-surface text-product-partner-content'
};
interface EntityTagProps {
  kind: EntityTagKind;
}
export function EntityTag(t0) {
  const $ = _c(20);
  const {
    kind
  } = t0;
  const t = useTranslations("search");
  const isNeutral = kind === "trader" || kind === "creator";
  let t1;
  bb0: {
    switch (kind) {
      case "market":
        {
          let t2;
          if ($[0] !== t) {
            t2 = t("tag_market");
            $[0] = t;
            $[1] = t2;
          } else {
            t2 = $[1];
          }
          t1 = t2;
          break bb0;
        }
      case "outcome":
        {
          let t2;
          if ($[2] !== t) {
            t2 = t("tag_outcome");
            $[2] = t;
            $[3] = t2;
          } else {
            t2 = $[3];
          }
          t1 = t2;
          break bb0;
        }
      case "winner":
        {
          let t2;
          if ($[4] !== t) {
            t2 = t("tag_winner");
            $[4] = t;
            $[5] = t2;
          } else {
            t2 = $[5];
          }
          t1 = t2;
          break bb0;
        }
      case "launchpad":
        {
          let t2;
          if ($[6] !== t) {
            t2 = t("tag_launchpad");
            $[6] = t;
            $[7] = t2;
          } else {
            t2 = $[7];
          }
          t1 = t2;
          break bb0;
        }
      case "trader":
        {
          let t2;
          if ($[8] !== t) {
            t2 = t("tag_trader");
            $[8] = t;
            $[9] = t2;
          } else {
            t2 = $[9];
          }
          t1 = t2;
          break bb0;
        }
      case "creator":
        {
          let t2;
          if ($[10] !== t) {
            t2 = t("tag_creator");
            $[10] = t;
            $[11] = t2;
          } else {
            t2 = $[11];
          }
          t1 = t2;
          break bb0;
        }
      case "partner":
        {
          let t2;
          if ($[12] !== t) {
            t2 = t("tag_partner");
            $[12] = t;
            $[13] = t2;
          } else {
            t2 = $[13];
          }
          t1 = t2;
          break bb0;
        }
    }
    t1 = undefined;
  }
  const label = t1;
  const t2 = isNeutral ? "outline" : "default";
  const t3 = TINT_CLASS[kind];
  let t4;
  if ($[14] !== t3) {
    t4 = cn("shrink-0 h-4 px-1.5 text-micro", t3);
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== label || $[17] !== t2 || $[18] !== t4) {
    t5 = <Badge variant={t2} className={t4}>{label}</Badge>;
    $[16] = label;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}